import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Search } from 'lucide-react';
import { Badge, Input } from '@dd/ui';
import { CATEGORY_CONFIG, type Category } from '@dd/shared';
import { usePaged } from '@/lib/hooks';
import { fmtD } from '@/lib/format';
import { Avatar, Chip, ErrorBox, Loading, LoadMore, PageHead, Panel, StatusBadge, Table } from '@/components/ui';

export interface AccountRow { id: string; ownerName: string; phone: string; createdAt: string; suspended: boolean; banned?: boolean; suspendedUntil?: string | null; warnings?: number; plan: string; status: string; shops: Array<{ id: string; name: string; category: Category }> }

export function Accounts() {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const q = sp.get('q') ?? '';
  const [term, setTerm] = useState(q);
  const setQ = (v: string) => setSp(v ? { q: v } : {});
  useEffect(() => { setTerm(q); }, [q]);
  const list = usePaged<AccountRow>('/accounts', { q: q || undefined, limit: 30 });
  return (
    <>
      <PageHead title="Customers" sub="Search by owner name, shop name, last digits of the phone, or account ID. Open a customer to see everything and act on the account." />
      <form className="mb-4 max-w-xl" onSubmit={(e) => { e.preventDefault(); setQ(term.trim()); }}>
        <Input prefix={<Search size={16} />} placeholder="Search accounts…" value={term} onChange={(e) => setTerm(e.target.value)} />
      </form>
      <Panel>
        {list.isLoading ? <Loading /> : list.error ? <ErrorBox error={list.error} retry={() => void list.refetch()} /> : (
          <>
            <Table rowKey={(r) => r.id} rows={list.items} onRow={(r) => nav(`/accounts/${r.id}`)} empty="No accounts match."
              cols={[
                { key: 'o', head: 'Owner', cell: (r) => <div className="flex items-center gap-3"><Avatar name={r.ownerName} size={36} /><div><div className="font-semibold">{r.ownerName}</div><div className="text-xs text-ink-3">{r.phone}</div></div></div> },
                { key: 's', head: 'Shops', cell: (r) => <div className="flex flex-wrap gap-1">{r.shops.map((s) => <Badge key={s.id} tone="neutral">{s.name} · {CATEGORY_CONFIG[s.category]?.labelEn ?? s.category}</Badge>)}</div> },
                { key: 'p', head: 'Plan', cell: (r) => <span className="font-semibold capitalize">{r.plan}</span> },
                { key: 'st', head: 'Status', cell: (r) => <div className="flex flex-wrap gap-1"><StatusBadge s={r.status} />{r.banned ? <Chip tone="bad">banned</Chip> : r.suspended && <Chip tone="bad">{r.suspendedUntil ? `suspended → ${fmtD(r.suspendedUntil)}` : 'suspended'}</Chip>}{!!r.warnings && <Chip tone="warn">{r.warnings} warning{r.warnings > 1 ? 's' : ''}</Chip>}</div> },
                { key: 'c', head: 'Joined', cell: (r) => fmtD(r.createdAt) },
              ]} />
            <LoadMore has={list.hasNextPage} loading={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()} />
          </>
        )}
      </Panel>
    </>
  );
}
